import { clientLogger } from '@/lib/clientLogger';
import { useState } from 'react';
import { withBackNavigation } from '@/lib/withBackNavigation';
import type { ModalLayer } from '@/lib/layerManager';
import { useMutation } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { X, DollarSign, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { showAlert } from '@/lib/capacitor';
import { NumericInput } from './ui/NumericInput';

interface TechnicianPayOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  currentAmount: number;
  onSuccess: () => void;
  layer?: ModalLayer;
}

function TechnicianPayOverrideModalBase({
  isOpen,
  onClose,
  jobId,
  currentAmount,
  onSuccess,
  layer,
}: TechnicianPayOverrideModalProps) {
  const [amount, setAmount] = useState<number | undefined>(currentAmount > 0 ? currentAmount : undefined);
  const [reason, setReason] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Override mutation
  const overrideMutation = useMutation({
    mutationFn: (data: { amount: number; reason?: string }) =>
      apiService.overrideTechnicianCut(jobId, data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      clientLogger.error('Failed to override compensation:', error);
      showAlert('Failed to apply override. Please try again.', 'Override Error');
    }
  });

  // Remove override mutation
  const removeOverrideMutation = useMutation({
    mutationFn: () => apiService.removeCompensationOverride(jobId),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      clientLogger.error('Failed to remove override:', error);
      showAlert('Failed to remove override. Please try again.', 'Error');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (amount === undefined) {
      newErrors.amount = 'Please enter a valid amount';
    } else if (amount < 0) {
      newErrors.amount = 'Amount cannot be negative';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    overrideMutation.mutate({
      amount: amount as number,
      reason: reason.trim() || undefined
    });
  };

  const handleRemoveOverride = () => {
    removeOverrideMutation.mutate();
  };

  const handleClose = () => {
    setAmount(currentAmount > 0 ? currentAmount : undefined);
    setReason('');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4" style={layer?.style}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Override Technician Pay</h2>
              <p className="text-sm text-gray-500">Set a custom compensation amount</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <strong>Warning:</strong> Manually overriding the compensation amount will replace the
                calculated value. The system will not automatically recalculate until you remove the override.
              </div>
            </div>
          </div>

          {/* Current Amount */}
          {currentAmount > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Amount</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatCurrency(currentAmount)}
                </span>
              </div>
            </div>
          )}

          {/* Override Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Override Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <NumericInput
                min="0"
                step="0.01"
                value={amount}
                onChange={(val) => {
                  setAmount(val);
                  if (errors.amount) setErrors(prev => ({ ...prev, amount: '' }));
                }}
                className={`w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${errors.amount ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="0.00"
              />
            </div>
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
            )}
          </div>

          {/* Reason Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
              placeholder="Enter the reason for this override..."
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be recorded for reference.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 space-y-3 keyboard-aware-footer">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={overrideMutation.isPending}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {overrideMutation.isPending && <LoadingSpinner />}
              Apply Override
            </button>
          </div>

          {/* Remove Override Button */}
          {currentAmount > 0 && (
            <button
              type="button"
              onClick={handleRemoveOverride}
              disabled={removeOverrideMutation.isPending}
              className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {removeOverrideMutation.isPending && <LoadingSpinner />}
              Remove Override & Recalculate
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export const TechnicianPayOverrideModal = withBackNavigation(TechnicianPayOverrideModalBase);

